import { getRBACScopeOverrideHeader } from "@/components/dev-toolbar-utils";
import { isProjectOverviewQueryKey } from "@/components/project/projectOverviewQuery";
import {
  capturePreservedStorageIfSafe,
  clearStorageForLogout,
  type PreservedStorage,
} from "@/lib/logout-storage";
import { getApiBaseURL } from "@/lib/utils";
import { datadogRum } from "@datadog/browser-rum";
import { Gram } from "@gram/client";
import { HTTPClient } from "@gram/client/lib/http.js";
import { GramProvider } from "@gram/client/react-query/_context.js";
import { QueryClientProvider } from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";
import { useTelemetry } from "./Telemetry";
import {
  IsPlatformAdminContext,
  SdkContext,
  queryClient,
  useProjectSlugForRequests,
  useSlugs,
} from "./Sdk";

const LOGOUT_PATH = "/rpc/auth.logout";

export const SdkProvider = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => {
  const projectSlug = useProjectSlugForRequests();
  const { projectSlug: pathProjectSlug } = useSlugs();
  const telemetry = useTelemetry();

  const isPlatformAdminRef = useRef(false);
  const previousProjectSlug = useRef(projectSlug);

  // Memoize the httpClient and gram instances
  const gram = useMemo(() => {
    // Values held across the logout round-trip. The logout response tells the
    // browser to wipe storage for the origin, which happens before the response
    // hook below runs, so anything meant to outlive the session has to be read
    // off localStorage before the request is sent. Keyed by request so
    // overlapping logouts — a double-clicked menu item — each restore their own
    // snapshot rather than racing over one slot. capturePreservedStorageIfSafe
    // returns the last safe snapshot while impersonating (it does not write
    // a new backup). The backup is written by a prior safe capture so
    // impersonation exit can time out and still restore after Clear-Site-Data.
    const preservedAcrossLogout = new WeakMap<Request, PreservedStorage>();

    const httpClient = new HTTPClient({
      fetcher: (request) => {
        const newRequest = new Request(request, {
          credentials: "include",
        });

        if (projectSlug && !newRequest.headers.get("gram-project")) {
          newRequest.headers.set("gram-project", projectSlug);
        }

        const scopeOverride = getRBACScopeOverrideHeader(
          import.meta.env.DEV || isPlatformAdminRef.current,
        );
        if (scopeOverride) {
          newRequest.headers.set("X-Gram-Scope-Override", scopeOverride);
        }

        return fetch(newRequest);
      },
    });

    httpClient.addHook("beforeRequest", (request) => {
      if (new URL(request.url).pathname === LOGOUT_PATH) {
        preservedAcrossLogout.set(request, capturePreservedStorageIfSafe());
      }
    });

    httpClient.addHook("response", (res, request) => {
      if (!res.ok) {
        return;
      }

      const u = new URL(request.url);
      if (u.pathname !== LOGOUT_PATH) {
        return;
      }

      datadogRum.stopSession();
      datadogRum.clearUser();
      telemetry.reset();
      document.cookie = "gram_admin_override=; path=/; max-age=0;";
      // Still clear explicitly: Clear-Site-Data is a no-op on origins the
      // browser does not treat as trustworthy, and on engines that don't
      // implement it. Where it did apply, storage is already empty and the
      // pre-request snapshot holds the only copy of the preserved entries.
      clearStorageForLogout(preservedAcrossLogout.get(request));
      preservedAcrossLogout.delete(request);
    });

    const gram = new Gram({
      serverURL: getApiBaseURL(),
      httpClient,
    });

    return gram;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- telemetry is stable context value; including it would recreate the SDK client unnecessarily
  }, [projectSlug, pathProjectSlug]);

  // Invalidate all queries when projectSlug changes; most keys are not
  // project-aware. Overview keys are, and must survive so the org-home
  // prefetch isn't discarded on navigation.
  useEffect(() => {
    if (previousProjectSlug.current !== projectSlug) {
      void queryClient.invalidateQueries({
        predicate: (query) => !isProjectOverviewQueryKey(query.queryKey),
      });
      previousProjectSlug.current = projectSlug;
    }
  }, [projectSlug]);

  return (
    <IsPlatformAdminContext.Provider value={isPlatformAdminRef}>
      <QueryClientProvider client={queryClient}>
        <GramProvider client={gram}>
          <SdkContext.Provider value={gram}>{children}</SdkContext.Provider>
        </GramProvider>
      </QueryClientProvider>
    </IsPlatformAdminContext.Provider>
  );
};
